import { ArrowForwardIcon, Box, CircularProgress, makeStyles, Theme, Typography } from '@freighthub/silhouette-core'
import {
  ISearchActivity,
  ISearchShipment,
  ITextSearchResult,
  ITextSearchResults,
  searchPointToString,
} from 'features/ShipmentsList'
import { FC, useMemo } from 'react'
import { useI18n } from 'utils/localisation'

import { SearchCategory } from './Search'
import { searchFieldLabels } from './SearchFieldNames'
import { TopBarSearchItem, TopBarSearchItemData } from './TopBarSearchItem'

const useStyles = makeStyles<Theme>((theme) => ({
  menu: {
    position: 'absolute',
    left: 0,
    right: 0,
    display: 'none',
    minHeight: theme.spacing(6),
    marginTop: theme.spacing(1),
    '& > div:first-child': {
      padding: 0,
    },
  },
  menuOpen: {
    display: 'block',
  },
  loaderWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    textAlign: 'center',
  },
  highlighted: {
    color: theme.palette.text.primary,
  },
  location: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dash: {
    margin: theme.spacing(0, 0.5),
  },
  arrow: {
    height: 18,
    width: 18,
    margin: theme.spacing(0, 0.5),
  },
}))

interface TopBarSearchResultsProps {
  isLoading: boolean
  results: ITextSearchResults
  onClickItem: () => void
}

export const TopBarSearchResults: FC<TopBarSearchResultsProps> = ({ isLoading, results, onClickItem }) => {
  const { translate } = useI18n()
  const classes = useStyles()

  const shipmentToOption = (shipment: ITextSearchResult<Partial<ISearchShipment>>): TopBarSearchItemData => {
    const { data, highlight } = shipment
    // @ts-expect-error TS(2769) FIXME: No overload matches this call.
    const matchingField = Object.keys(highlight)[0]
    const isMatchingFieldId = matchingField === 'id'
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    const html = highlight[matchingField][0]

    const highlightedText = (
      <span className={classes.highlighted}>
        <span dangerouslySetInnerHTML={{ __html: html }} />
      </span>
    )

    return {
      category: SearchCategory.Shipments,
      primaryText: isMatchingFieldId ? (
        <Box display="flex" alignItems="center" overflow="hidden">
          {highlightedText}
          <span className={classes.dash}>-</span>
          {/* @ts-expect-error TS(2345) FIXME: Argument of type 'ISearchPoint | undefined' is not... Remove this comment to see the full error message */}
          <span className={classes.location}>{searchPointToString(data._origin)}</span>
          <ArrowForwardIcon className={classes.arrow} />
          {/* @ts-expect-error TS(2345) FIXME: Argument of type 'ISearchPoint | undefined' is not... Remove this comment to see the full error message */}
          <span className={classes.location}>{searchPointToString(data._destination)}</span>
        </Box>
      ) : (
        <>
          <span>{translate(searchFieldLabels[matchingField])}</span>
          :&nbsp;
          {highlightedText}
        </>
      ),
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      secondaryText: `${translate('search-shipment-id')}: ${data.id}`,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      path: `/shipment/${data.id}/overview`,
    }
  }

  const messageToOption = (message: ITextSearchResult<ISearchActivity>): TopBarSearchItemData => {
    const { data, highlight } = message

    return {
      category: SearchCategory.Messages,
      primaryText: (
        <>
          <span>{translate('search-feed-label')}</span>
          :&nbsp;
          <span className={classes.highlighted}>
            {/* @ts-expect-error TS(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message */}
            <span dangerouslySetInnerHTML={{ __html: highlight.content }} />
          </span>
        </>
      ),
      secondaryText: `${translate('search-shipment-id')}: ${data.referenceId}`,
      path: `/shipment/${data.referenceId}/activityfeed`,
    }
  }

  const resultsToOptions = (results: ITextSearchResults): TopBarSearchItemData[] => {
    const { shipments, messages } = results
    return [...shipments.map(shipmentToOption), ...messages.map(messageToOption)]
  }

  const options = useMemo(() => resultsToOptions(results), [results])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" className={classes.loaderWrapper}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (options.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" className={classes.loaderWrapper}>
        <Typography color="textSecondary">{translate('search-result-not-found')}</Typography>
      </Box>
    )
  }

  return (
    <>
      {options.map((option, index) => (
        <TopBarSearchItem key={index} data={option} onClick={onClickItem} />
      ))}
    </>
  )
}
